import { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { 
  CreditCard, AlertTriangle, CheckCircle2, Eye, EyeOff, 
  Shield, ExternalLink, RefreshCw, Key, Loader2, Info,
  Unlink, Zap, Store
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CloverSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showSecrets, setShowSecrets] = useState({});
  
  const [clover, setClover] = useState({
    merchant_id: '',
    api_token: '',
    app_id: '',
    app_secret: '',
    enabled: false,
    connected_at: null,
    connection_status: 'not_connected'
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${API_URL}/api/clover/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data) {
        setClover(response.data);
      }
    } catch (error) {
      console.error('Error fetching Clover settings:', error);
      // Don't show error if just not configured
      if (error.response?.status !== 404) {
        toast.error('Failed to load Clover settings');
      }
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      await axios.put(`${API_URL}/api/clover/config`, clover, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Clover settings saved successfully');
      fetchSettings();
    } catch (error) {
      console.error('Error saving Clover settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    try {
      setTesting(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.post(`${API_URL}/api/clover/test-connection`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.status === 'connected') {
        toast.success(`Connected to ${response.data.merchant_name}`);
        setClover(prev => ({ ...prev, connection_status: 'connected' }));
      } else {
        toast.error(response.data.message || 'Connection failed');
        setClover(prev => ({ ...prev, connection_status: 'error' }));
      }
    } catch (error) {
      console.error('Error testing connection:', error);
      toast.error(error.response?.data?.detail || 'Connection test failed');
      setClover(prev => ({ ...prev, connection_status: 'error' }));
    } finally {
      setTesting(false);
    }
  };

  const disconnectClover = async () => {
    if (!confirm('Are you sure you want to disconnect Clover? Active subscriptions will continue but new ones cannot be created.')) {
      return;
    }
    
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      await axios.get(`${API_URL}/api/clover/disconnect`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Clover disconnected successfully');
      setClover({
        merchant_id: '',
        api_token: '',
        app_id: '',
        app_secret: '',
        enabled: false,
        connected_at: null,
        connection_status: 'not_connected'
      });
    } catch (error) {
      console.error('Error disconnecting Clover:', error);
      toast.error('Failed to disconnect Clover');
    } finally {
      setSaving(false);
    }
  };

  const toggleShowSecret = (key) => {
    setShowSecrets(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const StatusBadge = () => {
    switch (clover.connection_status) {
      case 'connected':
        return (
          <Badge variant="outline" className="bg-green-100 text-green-700">
            <CheckCircle2 className="h-3 w-3 mr-1" />
            Connected
          </Badge>
        );
      case 'error':
        return (
          <Badge variant="outline" className="bg-red-100 text-red-700">
            <AlertTriangle className="h-3 w-3 mr-1" />
            Connection Error
          </Badge>
        );
      default:
        return (
          <Badge variant="outline" className="bg-gray-100 text-gray-600">
            <AlertTriangle className="h-3 w-3 mr-1" />
            Not Connected
          </Badge>
        );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <Loader2 className="h-6 w-6 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Store className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <CardTitle>Clover</CardTitle>
              <CardDescription>POS and payment processing platform</CardDescription>
            </div>
          </div>
          <StatusBadge />
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Info Banner */}
        <Alert className="bg-green-50 border-green-200">
          <Info className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800">
            <strong>Clover Integration</strong>: Connect your existing Clover merchant account to process 
            membership payments. You'll need your <strong>Merchant ID</strong> and <strong>API Token</strong> from 
            your Clover Dashboard.
          </AlertDescription>
        </Alert>

        {/* Enable/Disable */}
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div>
            <Label className="font-medium">Enable Clover</Label>
            <p className="text-sm text-gray-500">Accept payments via Clover</p>
          </div>
          <Switch
            checked={clover.enabled}
            onCheckedChange={(checked) => setClover({ ...clover, enabled: checked })}
          />
        </div>

        {/* Required Fields */}
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="clover_merchant_id">
              Merchant ID <span className="text-red-500">*</span>
            </Label>
            <div className="relative">
              <Store className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                id="clover_merchant_id"
                value={clover.merchant_id}
                onChange={(e) => setClover({ ...clover, merchant_id: e.target.value })}
                placeholder="Your Clover Merchant ID"
                className="pl-10"
              />
            </div>
            <p className="text-xs text-gray-500">
              Find this in your Clover Dashboard → Account & Setup → Business Information
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="clover_api_token">
              API Token <span className="text-red-500">*</span>
            </Label>
            <div className="relative">
              <Key className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                id="clover_api_token"
                type={showSecrets.clover_api_token ? 'text' : 'password'}
                value={clover.api_token}
                onChange={(e) => setClover({ ...clover, api_token: e.target.value })}
                placeholder="Your API Token"
                className="pl-10 pr-10"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-1 top-1"
                onClick={() => toggleShowSecret('clover_api_token')}
              >
                {showSecrets.clover_api_token ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
            </div>
            <p className="text-xs text-gray-500">
              Generate an API Token in Clover Dashboard → Account & Setup → API Tokens
            </p>
          </div>
        </div>

        {/* Optional OAuth Fields (Collapsible) */}
        <details className="group">
          <summary className="cursor-pointer text-sm font-medium text-gray-600 hover:text-gray-900 flex items-center gap-2">
            <span className="group-open:rotate-90 transition-transform">▶</span>
            Advanced Settings (Optional - for OAuth)
          </summary>
          <div className="mt-4 space-y-4 pl-4 border-l-2 border-gray-200">
            <div className="space-y-2">
              <Label htmlFor="clover_app_id">Application ID</Label>
              <Input
                id="clover_app_id"
                value={clover.app_id}
                onChange={(e) => setClover({ ...clover, app_id: e.target.value })}
                placeholder="Optional - for OAuth integration"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="clover_app_secret">Application Secret</Label>
              <div className="relative">
                <Input
                  id="clover_app_secret"
                  type={showSecrets.clover_app_secret ? 'text' : 'password'}
                  value={clover.app_secret}
                  onChange={(e) => setClover({ ...clover, app_secret: e.target.value })}
                  placeholder="Optional - for OAuth integration"
                  className="pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1"
                  onClick={() => toggleShowSecret('clover_app_secret')}
                >
                  {showSecrets.clover_app_secret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          </div>
        </details>

        {/* Connection Info */}
        {clover.connected_at && (
          <div className="p-4 bg-green-50 rounded-lg border border-green-200">
            <div className="flex items-center gap-2 text-green-700">
              <CheckCircle2 className="h-4 w-4" />
              <span className="text-sm font-medium">
                Connected since {new Date(clover.connected_at).toLocaleDateString()}
              </span>
            </div>
          </div>
        )}

        {/* Help Links */}
        <div className="flex flex-wrap gap-3 text-sm">
          <a 
            href="https://www.clover.com/appmarket" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-teal-600 hover:text-teal-800"
          >
            <ExternalLink className="h-3 w-3" />
            Clover App Market
          </a>
          <a 
            href="https://docs.clover.com/docs/generate-a-test-api-token" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-teal-600 hover:text-teal-800"
          >
            <ExternalLink className="h-3 w-3" />
            How to get API Token
          </a>
          <a 
            href="https://sandbox.dev.clover.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-teal-600 hover:text-teal-800"
          >
            <ExternalLink className="h-3 w-3" />
            Sandbox Environment
          </a>
        </div>
      </CardContent>
      <CardFooter className="flex flex-col sm:flex-row gap-3 justify-between border-t pt-6">
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={testConnection}
            disabled={testing || !clover.merchant_id || !clover.api_token}
          >
            {testing ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Zap className="h-4 w-4 mr-2" />
            )}
            Test Connection
          </Button>
          
          {clover.connection_status === 'connected' && (
            <Button
              variant="outline"
              onClick={disconnectClover}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <Unlink className="h-4 w-4 mr-2" />
              Disconnect
            </Button>
          )}
        </div>
        
        <Button 
          onClick={saveSettings}
          disabled={saving || !clover.merchant_id || !clover.api_token}
          className="bg-green-600 hover:bg-green-700"
        >
          {saving ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Shield className="h-4 w-4 mr-2" />
          )}
          Save Clover Settings
        </Button>
      </CardFooter>
    </Card>
  );
};

export default CloverSettings;
